import { css } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'
import { useMemo } from 'react'

import { useFormatPrice } from '@src/blocks/currency'
import { getOfferPricesForProductCard } from '@src/quince-site-common/src/utils/festivalOffersUtility'

import type { ProductTilePriceComputed, ProductTilePriceProps } from './price.types'
import type { SystemStyleObject } from '@lastbrand/web-ui-styled-system/types'

const PRICE_TEXT_STYLE = {
  mini: 'body-12',
  compact: { base: 'body-12', lg: 'body-14' },
  default: { base: 'body-12', lg: 'body-16' },
} as const

const PRICE_SALE_COLOR = {
  // eslint-disable-next-line i18next/no-literal-string -- DS color token, not translatable copy
  promo: 'promo' as const,
  // eslint-disable-next-line i18next/no-literal-string -- DS color token, not translatable copy
  neutral: 'neutral' as const,
} as const

function hasRenderablePriceCompare(node: string | undefined): boolean {
  if (node == null) return false
  return node.trim().length > 0
}

export function useProductTilePrice({
  selectedVariant,
  subscriptionEnabled = false,
  productPromoConfig,
}: ProductTilePriceProps): ProductTilePriceComputed {
  const formatPrice = useFormatPrice()

  return useMemo(() => {
    const salePrice = selectedVariant?.price?.salePrice
    const showFrom = selectedVariant?.price?.isRange
    const formattedStandardPrice = formatPrice(salePrice ?? 0)

    const { festivalPrice, subscriptionPrice } = getOfferPricesForProductCard(
      subscriptionEnabled,
      selectedVariant,
      productPromoConfig,
    )
    const offerPrice = subscriptionPrice ?? festivalPrice
    const formattedOfferPrice = offerPrice ? formatPrice(offerPrice) : undefined
    const hasOffer = Boolean(formattedOfferPrice)

    const priceSale = formattedOfferPrice ?? formattedStandardPrice
    const priceCompareAt = hasOffer ? formattedStandardPrice : undefined
    const priceSaleColor = hasOffer ? PRICE_SALE_COLOR.promo : PRICE_SALE_COLOR.neutral
    const showPriceFrom = Boolean(showFrom && !offerPrice)

    return {
      priceSale,
      priceCompareAt,
      priceSaleColor,
      showPriceFrom,
      hasOffer,
      formattedStandardPrice,
      formattedOfferPrice,
    }
  }, [formatPrice, productPromoConfig, selectedVariant, subscriptionEnabled])
}

/**
 * Product tile price row aligned with {@link ProductTile} typography (default / compact / mini).
 * Prefer {@link useProductTilePrice} when you only need computed strings.
 */
export function ProductTilePrice({ tileVariant = 'default', className, ...priceInputs }: ProductTilePriceProps) {
  const { t } = useTranslation('common')
  const computed = useProductTilePrice(priceInputs)
  const priceFromLabel = t('from')

  const isMini = tileVariant === 'mini'
  const isCompact = tileVariant === 'compact'
  const isPromoPrice = computed.priceSaleColor === PRICE_SALE_COLOR.promo
  /** Matches @lastbrand/web-ui/panda-config/text-styles.ts `body-12` | `body-14` | `body-16` (responsive). */
  const priceTextStyle = (
    isMini ? PRICE_TEXT_STYLE.mini : isCompact ? PRICE_TEXT_STYLE.compact : PRICE_TEXT_STYLE.default
  ) as SystemStyleObject['textStyle']

  const priceSaleNode = computed.priceSale
  const priceCompareAtNode = computed.priceCompareAt

  if (isMini) {
    return (
      <div
        className={css(
          {
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
            gap: 'space-4',
            width: '100%',
            minWidth: 0,
          },
          className,
        )}
      >
        {computed.showPriceFrom ? (
          <span
            className={css({
              textStyle: priceTextStyle,
              color: computed.priceSaleColor === PRICE_SALE_COLOR.neutral ? 'black' : 'red-100',
              flexShrink: 0,
            })}
          >
            {priceFromLabel}
          </span>
        ) : null}
        <span
          className={css({
            textStyle: priceTextStyle,
            color: computed.priceSaleColor === PRICE_SALE_COLOR.neutral ? 'black' : 'red-100',
            flexShrink: 0,
          })}
        >
          {priceSaleNode}
        </span>
        {hasRenderablePriceCompare(priceCompareAtNode) ? (
          <span
            className={css({
              textStyle: priceTextStyle,
              color: 'mediumgray',
              textDecoration: 'line-through',
              textDecorationSkipInk: 'none',
              flexShrink: 0,
            })}
          >
            {priceCompareAtNode}
          </span>
        ) : null}
      </div>
    )
  }

  return (
    <div
      className={css(
        {
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: { base: isPromoPrice ? 'flex-end' : 'flex-start', lg: 'flex-end' },
          textAlign: { base: isPromoPrice ? 'right' : 'left', lg: 'right' },
          whiteSpace: 'nowrap',
        },
        className,
      )}
    >
      <div
        className={css({
          display: 'flex',
          flexDirection: { base: isPromoPrice ? 'row' : 'column', lg: 'row' },
          gap: { base: isPromoPrice ? 'space-2' : 0, lg: 'space-2' },
          alignItems: 'flex-start',
          justifyContent: { base: isPromoPrice ? 'flex-end' : undefined, lg: undefined },
          textStyle: priceTextStyle,
          color: computed.priceSaleColor === PRICE_SALE_COLOR.neutral ? 'black' : 'red-100',
        })}
      >
        {computed.showPriceFrom ? <span>{priceFromLabel}</span> : null}
        <span>{priceSaleNode}</span>
      </div>
      {hasRenderablePriceCompare(priceCompareAtNode) ? (
        <div
          className={css({
            textStyle: priceTextStyle,
            color: 'mediumgray',
            textDecoration: 'line-through',
            textDecorationSkipInk: 'none',
          })}
        >
          {priceCompareAtNode}
        </div>
      ) : null}
    </div>
  )
}
